import { inject, Injectable } from '@angular/core';
import { AppFileViewer } from '@intellio/shared/models';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseService } from './base-service.service';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from './app-config.service';
import { AuthService } from './auth.service';

// Preserve the service-level import for callers that imported this contract directly.
export { AppFileViewer } from '@intellio/shared/models';

@Injectable({
  providedIn: 'root',
})
export class AppFileViewerService extends BaseService {
  private fileViewer?: AppFileViewer;
  private visible$ = new BehaviorSubject<boolean>(false);

  constructor() {
    super(inject(HttpClient), inject(AppConfigService), inject(AuthService));
  }

  attach(viewer: AppFileViewer) {
    this.fileViewer = viewer;
  }

  get viewer() {
    return this.fileViewer;
  }

  get visibleChanges$(): Observable<boolean> {
    return this.visible$.asObservable();
  }

  // Streams a generated PDF preview from an intelligent-action result.
  getIntelligentActionFile(
    applicationId: string,
    fileName: string
  ): Observable<Blob> {
    const endpointURL = `/api/agents/documents/previews/${applicationId}/${fileName}`;
    return this.getWithoutResponseBase(endpointURL, { responseType: 'blob' });
  }

  // Streams a generated PDF preview for an application file.
  getApplicationFilePreview(
    applicationId: string,
    fileName: string
  ): Observable<Blob> {
    const endpointURL = `/api/applications/${applicationId}/files/pdf-preview/${fileName}`;
    return this.getWithoutResponseBase(endpointURL, { responseType: 'blob' });
  }

  show() {
    const fileViewer = this.viewer;
    if (!fileViewer) {
      console.warn('Wait to view file');
      return;
    }

    if (!fileViewer.isOpen) {
      fileViewer.isOpen = true;
      this.visible$.next(true);
    }
  }

  hide() {
    const fileViewer = this.viewer;
    if (!fileViewer) {
      return;
    }
    if (fileViewer.isOpen) {
      fileViewer.isOpen = false;
      this.visible$.next(false);
    }
  }

  setFile(filePath: string) {
    if (!this.fileViewer) {
      console.warn('Trying to set file when viewer does not exist.');
      return;
    }
    this.fileViewer.filePath = filePath;
  }

  get isVisible() {
    return this.visible$.value;
  }
}
